/**
 * Role validation utilities for access control
 */

// Define role hierarchy levels
export const ROLE_LEVELS = {
  job_seeker: 1,
  hiring_manager: 2,
  company_admin: 3,
  admin: 4
};

// Define role names for display
export const ROLE_NAMES = {
  job_seeker: 'Job Seeker',
  hiring_manager: 'Hiring Manager',
  company_admin: 'Company Admin',
  admin: 'Administrator'
};

// Define feature permissions by role
export const FEATURE_PERMISSIONS = {
  // Job Seeker Features
  apply_to_jobs: ['job_seeker'],
  save_jobs: ['job_seeker'],
  view_applications: ['job_seeker'],
  view_recommendations: ['job_seeker'],
  manage_profile: ['job_seeker', 'hiring_manager', 'company_admin', 'admin'],
  
  // Employer Features
  post_jobs: ['hiring_manager', 'company_admin', 'admin'],
  manage_jobs: ['hiring_manager', 'company_admin', 'admin'],
  view_candidates: ['hiring_manager', 'company_admin', 'admin'],
  review_applications: ['hiring_manager', 'company_admin', 'admin'],
  schedule_interviews: ['hiring_manager', 'company_admin', 'admin'],
  
  // Company Admin Features
  manage_team: ['company_admin', 'admin'],
  manage_company_profile: ['company_admin', 'admin'],
  manage_locations: ['company_admin', 'admin'],
  view_analytics: ['company_admin', 'admin'],
  manage_billing: ['company_admin', 'admin'],
  
  // Admin Features
  access_all_companies: ['admin'],
  manage_system_settings: ['admin']
};

/**
 * Check if a role has access to a specific feature
 */
export const hasFeatureAccess = (userRole, feature) => {
  const allowedRoles = FEATURE_PERMISSIONS?.[feature];
  return allowedRoles?.includes(userRole) || false;
};

/**
 * Check if user role is valid
 */
export const isValidRole = (role) => {
  return Object.keys(ROLE_LEVELS)?.includes(role);
};

/**
 * Get role display name
 */
export const getRoleName = (role) => {
  return ROLE_NAMES?.[role] || 'Unknown Role';
};

/**
 * Compare role levels
 */
export const compareRoles = (role1, role2) => {
  const level1 = ROLE_LEVELS?.[role1] || 0;
  const level2 = ROLE_LEVELS?.[role2] || 0;
  return level1 - level2;
};

/**
 * Check if role1 has higher or equal level than role2
 */
export const hasMinimumRoleLevel = (role1, role2) => {
  return compareRoles(role1, role2) >= 0;
};

/**
 * Get features available to a role
 */
export const getAvailableFeatures = (role) => {
  return Object.keys(FEATURE_PERMISSIONS)?.filter(feature => 
    hasFeatureAccess(role, feature)
  );
};

/**
 * Validate if user can perform action on resource
 */
export const canPerformAction = (userRole, action, resourceOwnerId, userId) => {
  // Admin can do anything
  if (userRole === 'admin') return true;
  
  // User can always manage their own resources
  if (resourceOwnerId === userId) return true;
  
  // Check feature permission
  return hasFeatureAccess(userRole, action);
};

export default {
  ROLE_LEVELS,
  ROLE_NAMES,
  FEATURE_PERMISSIONS,
  hasFeatureAccess,
  isValidRole,
  getRoleName,
  compareRoles,
  hasMinimumRoleLevel,
  getAvailableFeatures,
  canPerformAction
};